import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { AuthUserContext } from "../../context/AuthUserContext";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useNavigation } from "../../utils/navigation";
import i18next from "i18next";

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  background-color: var(--bg-100);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-medium);
  padding: var(--spacing-xs);
`;

const AvatarChip = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-200);
  color: var(--bg-100);
  font-size: var(--font-size-base);
  text-transform: uppercase;

  @media (max-width: 400px) {
    display: none;
  }
`;

const Divider = styled.div`
  flex-shrink: 0;
  width: 1px;
  align-self: stretch;
  background-color: var(--bg-300);
  margin: var(--spacing-xxs) 0;

  @media (max-width: 400px) {
    display: none;
  }
`;

const Tabs = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xxs);
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  white-space: nowrap;
  border: none;
  border-radius: var(--border-radius-large);
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: var(--font-size-small);
  text-transform: uppercase;
  background-color: ${(props) =>
    props.$active ? "var(--primary-200)" : "transparent"};
  color: ${(props) => (props.$active ? "var(--bg-100)" : "var(--text-200)")};
  &:hover {
    background-color: ${(props) =>
      props.$active ? "var(--primary-100)" : "var(--bg-200)"};
    color: ${(props) => (props.$active ? "var(--bg-100)" : "var(--text-100)")};
  }
`;

const TabIcon = styled.svg`
  flex-shrink: 0;
`;

const LogoutDivider = styled(Divider)`
  @media (max-width: 400px) {
    display: block;
  }
`;

const LogoutButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  height: 40px;
  padding: 0 var(--spacing-md);
  border-radius: var(--border-radius-large);
  border: none;
  background-color: #fbdcdc;
  color: var(--error-color);
  font-size: var(--font-size-small);
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;
  &:hover {
    background-color: var(--error-color);
    color: var(--bg-100);
  }

  @media (max-width: 560px) {
    width: 40px;
    padding: 0;
    span {
      display: none;
    }
  }
`;

const ConfirmContainer = styled.div`
  background-color: #00000031;
  position: fixed;
  left: 0;
  top: 0;
  z-index: var(--zindex-modal);
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ConfirmWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  background-color: var(--bg-100);
  padding: var(--spacing-xxl) var(--spacing-lg);
  width: 94%;
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-large);
  text-align: center;
  @media (min-width: 768px) {
    width: 420px;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xl);
`;
const ButtonTransparent = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-large);
  cursor: pointer;
  color: var(--primary-200);
  background-color: var(--bg-200);
  border: none;
  &:hover {
    color: var(--bg-100);
    background-color: var(--primary-200);
  }
`;
const ButtonConfirm = styled(ButtonTransparent)`
  color: var(--bg-100);
  background-color: var(--error-color);
  &:hover {
    opacity: 0.85;
    background-color: var(--error-color);
  }
`;

const TABS = [
  { key: "account", labelKey: "PROFILE.ACCOUNT_OVERVIEW" },
  { key: "profile", labelKey: "PROFILE.PROFILE_INFO" },
  { key: "addresses", labelKey: "PROFILE.MANAGE_ADDRESSES" },
  { key: "order_history", labelKey: "PROFILE.ORDERS" },
  { key: "cards", labelKey: "PROFILE.SAVED_CARDS" },
  { key: "referrals", labelKey: "PROFILE.REFERRALS" },
];

const UserMenu = () => {
  const { t } = useTranslation();
  const { userProfile, logoutUser } = useContext(AuthUserContext);
  const [selectedItem, setSelectedItem] = useState("account");
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();
  const {
    goToProfile,
    goToAccount,
    goToAddressBook,
    goToSavedCards,
    goToOrderHistory,
  } = useNavigation();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === `/${i18next.language}/account/profile`) {
      setSelectedItem("profile");
    } else if (location.pathname === `/${i18next.language}/account/cards`) {
      setSelectedItem("cards");
    } else if (
      location.pathname.includes(`/${i18next.language}/account/address-book`)
    ) {
      setSelectedItem("addresses");
    } else if (
      location.pathname.includes(`/${i18next.language}/account/orders`)
    ) {
      setSelectedItem("order_history");
    } else {
      setSelectedItem("account");
    }
  }, [location.pathname, i18next.language]);

  const handleTabClick = (key) => {
    setSelectedItem(key);
    switch (key) {
      case "account":
        navigate(goToAccount());
        break;
      case "profile":
        navigate(goToProfile());
        break;
      case "addresses":
        navigate(goToAddressBook());
        break;
      case "order_history":
        navigate(goToOrderHistory());
        break;
      case "cards":
        navigate(goToSavedCards());
        break;
      default:
        break;
    }
  };

  const initials = userProfile?.first_name
    ? userProfile.first_name.charAt(0)
    : "";

  return (
    <>
      <Nav>
        <AvatarChip>{initials}</AvatarChip>
        <Divider />
        <Tabs>
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              $active={selectedItem === tab.key}
              onClick={() => handleTabClick(tab.key)}
            >
              {t(tab.labelKey)}
            </Tab>
          ))}
        </Tabs>
        <LogoutDivider />
        <LogoutButton onClick={() => setShowConfirm(true)} title={t("PROFILE.LOGOUT")}>
          <TabIcon
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16 17L21 12L16 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 12H9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </TabIcon>
          <span>{t("PROFILE.LOGOUT")}</span>
        </LogoutButton>
      </Nav>
      {showConfirm && (
        <ConfirmContainer>
          <ConfirmWrapper>
            {t("PROFILE.CONFIRM_LOGOUT")}
            <ButtonContainer>
              <ButtonConfirm className="button-transparent" onClick={logoutUser}>
                {t("BUTTONS.LOGOUT")}
              </ButtonConfirm>
              <ButtonTransparent
                className="button-transparent"
                onClick={() => setShowConfirm(false)}
              >
                {t("BUTTONS.CANCEL")}
              </ButtonTransparent>
            </ButtonContainer>
          </ConfirmWrapper>
        </ConfirmContainer>
      )}
    </>
  );
};

export default UserMenu;
